/**
 * FeedbackOverlay
 *
 * ユーザーフィードバック用のオーバーレイUI
 * - ローディング / プログレス
 * - エラー / 警告 / 情報 / 成功メッセージ
 *
 * すべて `styles.css` の共通デザイントークン（白黒シンプル）に準拠する。
 * 絵文字は使わずタイポグラフィで意味を伝える設計。
 */

const AUTO_HIDE_DEFAULT_MS = 3000;

export class FeedbackOverlay {
  /**
   * @param {Object} options
   * @param {HTMLElement} [options.container=document.body]
   * @param {number} [options.autoHideDelay=3000]
   */
  constructor(options = {}) {
    this.container = options.container || document.body;
    this.autoHideDelay = options.autoHideDelay || AUTO_HIDE_DEFAULT_MS;

    this.overlayElement = null;
    this.cardElement = null;

    this.isVisible = false;
    this.currentType = null; // loading / error / warning / info / success
    this.autoHideTimeout = null;

    this._createOverlay();
    console.log('FeedbackOverlay created');
  }

  /**
   * @private
   */
  _createOverlay() {
    this.overlayElement = document.createElement('div');
    this.overlayElement.id = 'feedback-overlay';
    this.overlayElement.className = 'feedback-overlay';

    this.cardElement = document.createElement('div');
    this.cardElement.className = 'feedback-card';

    this.overlayElement.appendChild(this.cardElement);
    this.container.appendChild(this.overlayElement);
  }

  /**
   * ローディング表示（プログレス任意）
   * @param {string} message
   * @param {number|null} progress - 0-100, null なら非表示
   */
  showLoading(message = '読み込み中...', progress = null) {
    this.currentType = 'loading';
    this._clearAutoHideTimeout();

    const parts = [
      '<div class="feedback-spinner" role="status" aria-label="読み込み中"></div>',
      `<div class="feedback-title">${escapeHtml(message)}</div>`,
    ];

    if (typeof progress === 'number' && progress >= 0 && progress <= 100) {
      parts.push(
        `<div class="feedback-progress-track"><div class="feedback-progress-fill" style="width:${progress}%"></div></div>`,
        `<div class="feedback-progress-label">${Math.round(progress)}%</div>`
      );
    }

    this.cardElement.innerHTML = parts.join('');
    this.show();
  }

  /**
   * エラーメッセージ表示（対処ヒント付き）
   * @param {string} message
   * @param {string[]} [hints]
   */
  showError(message, hints = []) {
    this.currentType = 'error';
    this._clearAutoHideTimeout();

    const parts = [`<div class="feedback-title">${escapeHtml(message)}</div>`];

    if (Array.isArray(hints) && hints.length > 0) {
      parts.push(this._renderHints('対処方法', hints));
    }

    parts.push('<button type="button" class="feedback-close">閉じる</button>');

    this.cardElement.innerHTML = parts.join('');

    const closeBtn = this.cardElement.querySelector('.feedback-close');
    if (closeBtn) {
      closeBtn.addEventListener('click', () => this.hide());
    }

    this.show();
  }

  /**
   * 警告メッセージ表示（一定時間後に自動非表示）
   * @param {string} message
   * @param {number} [duration=3000]
   */
  showWarning(message, duration = AUTO_HIDE_DEFAULT_MS) {
    this.currentType = 'warning';
    this._showTransient(message, duration);
  }

  /**
   * 情報メッセージ表示
   * @param {string} message
   * @param {number} [duration=3000]
   */
  showInfo(message, duration = AUTO_HIDE_DEFAULT_MS) {
    this.currentType = 'info';
    this._showTransient(message, duration);
  }

  /**
   * 成功メッセージ表示
   * @param {string} message
   * @param {number} [duration=2000]
   */
  showSuccess(message, duration = 2000) {
    this.currentType = 'success';
    this._showTransient(message, duration);
  }

  show() {
    this.overlayElement.classList.add('is-visible');
    this.isVisible = true;
  }

  hide() {
    this.overlayElement.classList.remove('is-visible');
    this.isVisible = false;
    this.currentType = null;
    this._clearAutoHideTimeout();
  }

  getState() {
    return {
      isVisible: this.isVisible,
      currentType: this.currentType,
    };
  }

  cleanup() {
    this._clearAutoHideTimeout();
    if (this.overlayElement && this.overlayElement.parentNode) {
      this.overlayElement.parentNode.removeChild(this.overlayElement);
    }
    console.log('FeedbackOverlay cleaned up');
  }

  // ---- private helpers ----

  _showTransient(message, duration) {
    this._clearAutoHideTimeout();
    this.cardElement.innerHTML = `<div class="feedback-title">${escapeHtml(message)}</div>`;
    this.show();
    if (duration > 0) {
      this.autoHideTimeout = setTimeout(() => this.hide(), duration);
    }
  }

  _renderHints(label, items) {
    const li = items.map((h) => `<li>${escapeHtml(h)}</li>`).join('');
    return `<div class="feedback-hints"><div class="feedback-hints-label">${escapeHtml(label)}</div><ul>${li}</ul></div>`;
  }

  _clearAutoHideTimeout() {
    if (this.autoHideTimeout) {
      clearTimeout(this.autoHideTimeout);
      this.autoHideTimeout = null;
    }
  }
}

/**
 * 最小限のHTMLエスケープ（メッセージにユーザー入力が混ざるケースの防御）
 * @param {string} str
 * @returns {string}
 */
function escapeHtml(str) {
  if (typeof str !== 'string') return '';
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}
